import { useState, useEffect } from 'react';
import api from '../../api/axios';
import { useToast } from '../../context/ToastContext';

const INTERESTED_IN_OPTIONS = ['Men', 'Women', 'Non-binary people', 'Everyone'];

const VACCINE_TYPES = [
  { key: 'covid_19', label: 'COVID-19' },
  { key: 'flu', label: 'Flu' },
  { key: 'hpv', label: 'HPV' },
  { key: 'hepatitis_a', label: 'Hepatitis A' },
  { key: 'hepatitis_b', label: 'Hepatitis B' },
  { key: 'tdap', label: 'Tdap' },
  { key: 'mmr', label: 'MMR' },
  { key: 'varicella', label: 'Varicella' },
  { key: 'meningococcal', label: 'Meningococcal' },
  { key: 'other', label: 'Other' },
];

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    zIndex: 60,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  panel: {
    width: '100%',
    maxWidth: '480px',
    maxHeight: '80vh',
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
    padding: '24px 20px',
    overflowY: 'auto',
    animation: 'slideInUp 300ms ease',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: { fontSize: '18px', fontWeight: '600' },
  section: { marginBottom: '24px' },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  rangeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  rangeInput: {
    flex: 1,
    accentColor: 'var(--accent)',
  },
  rangeValue: {
    fontSize: '14px',
    fontWeight: '500',
    minWidth: '40px',
    textAlign: 'right',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid var(--border-color)',
  },
  saving: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    textAlign: 'center',
    padding: '4px',
  },
};

/**
 * FilterPanel — shared by Discover and Wall pages.
 *
 * Props:
 *   filters  — current filter state (used to initialize controls)
 *   onApply  — called with the new filter object when user taps Apply
 *   onClose  — called to dismiss the panel
 */
export default function FilterPanel({ filters, onApply, onClose }) {
  const { showToast } = useToast();
  const [minAge, setMinAge] = useState(filters?.min_age || 18);
  const [maxAge, setMaxAge] = useState(filters?.max_age || 50);
  const [maxDistance, setMaxDistance] = useState(filters?.max_distance || 50);
  const [genderPref, setGenderPref] = useState(filters?.gender_pref || []);
  const [vaccinePref, setVaccinePref] = useState(filters?.vaccine_pref || []);
  const [saving, setSaving] = useState(false);

  // Sync with incoming filters prop (e.g., when loaded from server)
  useEffect(() => {
    if (filters) {
      if (filters.min_age != null) setMinAge(filters.min_age);
      if (filters.max_age != null) setMaxAge(filters.max_age);
      if (filters.max_distance != null) setMaxDistance(filters.max_distance);
      if (filters.gender_pref) setGenderPref(filters.gender_pref);
      if (filters.vaccine_pref) setVaccinePref(filters.vaccine_pref);
    }
  }, [filters]);

  const toggleGender = (value) => {
    setGenderPref((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleVaccine = (key) => {
    setVaccinePref((prev) =>
      prev.includes(key) ? prev.filter((v) => v !== key) : [...prev, key]
    );
  };

  const handleApply = async () => {
    const newFilters = {
      min_age: minAge,
      max_age: maxAge,
      max_distance: maxDistance,
      gender_pref: genderPref,
      vaccine_pref: vaccinePref,
    };

    // Save to server for persistence across sessions
    setSaving(true);
    try {
      await api.put('/users/filters', newFilters);
    } catch {
      // save failed, still apply locally
      showToast('Couldn\'t save filters', 'error');
    }
    setSaving(false);

    onApply(newFilters);
    onClose();
  };

  const handleReset = async () => {
    // Clear saved filters on the server, then re-fetch defaults
    // (server returns profile interested_in as default gender_pref)
    try {
      await api.put('/users/filters', {
        min_age: 18,
        max_age: 50,
        max_distance: 50,
        gender_pref: [],
        vaccine_pref: [],
      });
      const res = await api.get('/users/filters');
      const defaults = res.data;
      setMinAge(defaults.min_age || 18);
      setMaxAge(defaults.max_age || 50);
      setMaxDistance(defaults.max_distance || 50);
      setGenderPref(defaults.gender_pref || []);
      setVaccinePref(defaults.vaccine_pref || []);
    } catch {
      // Fallback if server call fails
      showToast('Couldn\'t reset filters', 'error');
      setMinAge(18);
      setMaxAge(50);
      setMaxDistance(50);
      setGenderPref([]);
      setVaccinePref([]);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Filters</h3>
          <button className="btn btn-ghost" onClick={handleReset}>
            Reset
          </button>
        </div>

        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Age range</h4>
          <div style={styles.rangeRow}>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Min</span>
            <input
              type="range"
              min={18}
              max={99}
              value={minAge}
              onChange={(e) => setMinAge(Math.min(parseInt(e.target.value), maxAge))}
              style={styles.rangeInput}
            />
            <span style={styles.rangeValue}>{minAge}</span>
          </div>
          <div style={styles.rangeRow}>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Max</span>
            <input
              type="range"
              min={18}
              max={99}
              value={maxAge}
              onChange={(e) => setMaxAge(Math.max(parseInt(e.target.value), minAge))}
              style={styles.rangeInput}
            />
            <span style={styles.rangeValue}>{maxAge}</span>
          </div>
        </div>

        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Maximum distance</h4>
          <div style={styles.rangeRow}>
            <input
              type="range"
              min={1}
              max={200}
              value={maxDistance}
              onChange={(e) => setMaxDistance(parseInt(e.target.value))}
              style={styles.rangeInput}
            />
            <span style={styles.rangeValue}>{maxDistance} mi</span>
          </div>
        </div>

        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Show me</h4>
          <div className="chip-group">
            {INTERESTED_IN_OPTIONS.map((g) => (
              <button
                key={g}
                className={`chip ${genderPref.includes(g) ? 'active' : ''}`}
                onClick={() => toggleGender(g)}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Vaccination preferences</h4>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px' }}>
            Only show people vaccinated for:
          </p>
          <div className="chip-group">
            {VACCINE_TYPES.map((v) => (
              <button
                key={v.key}
                className={`chip ${vaccinePref.includes(v.key) ? 'active' : ''}`}
                onClick={() => toggleVaccine(v.key)}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.actions}>
          <button className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleApply}
            style={{ flex: 1 }}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Apply filters'}
          </button>
        </div>
      </div>
    </div>
  );
}
